
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSearch, FiX, FiArrowRight, FiFileText, FiTool, FiDownload, FiStar, FiMusic } from 'react-icons/fi';
import { searchSite, SearchResult } from '../utils/searchEngine';
import { useNavigate } from 'react-router-dom';

export const SearchBar: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<SearchResult[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement>(null);

    // Toggle search with Cmd+K or Ctrl+K
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setIsOpen((prev) => !prev);
            }
            if (e.key === 'Escape') {
                setIsOpen(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Set focus when opened
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 100);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
            setQuery('');
            setResults([]);
        }
    }, [isOpen]);

    // Handle search logic
    useEffect(() => {
        const searchResults = searchSite(query);
        setResults(searchResults as SearchResult[]);
        setSelectedIndex(0);
    }, [query]);

    const handleSelect = (result: SearchResult) => {
        setIsOpen(false);
        navigate(result.url);
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            setSelectedIndex((prev) => (prev + 1) % results.length);
        } else if (e.key === 'ArrowUp') {
            setSelectedIndex((prev) => (prev - 1 + results.length) % results.length);
        } else if (e.key === 'Enter' && results[selectedIndex]) {
            handleSelect(results[selectedIndex]);
        }
    };

    const getIcon = (type: string) => {
        switch (type) {
            case 'tool': return <FiTool className="text-sky-400" />;
            case 'download': return <FiDownload className="text-emerald-400" />;
            case 'project': return <FiStar className="text-amber-400" />;
            case 'music': return <FiMusic className="text-purple-400" />;
            default: return <FiFileText className="text-blue-400" />;
        }
    };

    return (
        <>
            {/* Search Toggle Button in Header */}
            <motion.button
                onClick={() => setIsOpen(true)}
                className="flex items-center space-x-2 px-3 py-1.5 rounded-full bg-zinc-800/50 hover:bg-zinc-700/50 border border-zinc-700/50 text-gray-400 hover:text-white transition-all duration-300 group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Search"
            >
                <FiSearch className="w-4 h-4 group-hover:text-sky-400 transition-colors" />
                <span className="hidden lg:inline text-xs font-medium">Search site...</span>
                <span className="hidden md:inline-flex items-center space-x-1 ml-2">
                    <kbd className="px-1.5 py-0.5 rounded bg-zinc-900 border border-zinc-700 text-[10px] font-sans">⌘</kbd>
                    <kbd className="px-1.5 py-0.5 rounded bg-zinc-900 border border-zinc-700 text-[10px] font-sans">K</kbd>
                </span>
            </motion.button>

            <AnimatePresence>
                {isOpen && (
                    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-20 px-4">
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsOpen(false)}
                            className="absolute inset-0 bg-black/80 backdrop-blur-md"
                        />

                        {/* Modal */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: -20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -20 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            className="relative w-full max-w-2xl bg-zinc-900/90 border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden"
                        >
                            {/* Search Input Area */}
                            <div className="flex items-center px-4 py-4 border-b border-zinc-800">
                                <FiSearch className="text-gray-400 w-5 h-5 mr-3" />
                                <input
                                    ref={inputRef}
                                    type="text"
                                    placeholder="What are you looking for?"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onKeyDown={onKeyDown}
                                    className="w-full bg-transparent text-white outline-none text-lg placeholder-gray-500"
                                />
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className="p-1 hover:bg-zinc-800 rounded transition-colors"
                                >
                                    <FiX className="text-gray-400 w-5 h-5" />
                                </button>
                            </div>

                            {/* Results Area */}
                            <div className="max-h-[60vh] overflow-y-auto custom-scrollbar">
                                {query.length === 0 ? (
                                    <div className="p-8 text-center text-gray-400">
                                        <p className="text-sm">Search for projects, tools, downloads, and more.</p>
                                        <div className="mt-4 flex flex-wrap justify-center gap-2">
                                            {['Timecode', 'DCP', 'Credits', 'Pro Tools', 'SoundLibrary'].map((tag) => (
                                                <button
                                                    key={tag}
                                                    onClick={() => setQuery(tag)}
                                                    className="px-3 py-1 rounded-full bg-zinc-800 hover:bg-zinc-700 text-xs transition-colors"
                                                >
                                                    {tag}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ) : results.length > 0 ? (
                                    <div className="py-2">
                                        {results.map((result, index) => (
                                            <div
                                                key={`${result.url}-${index}`}
                                                onClick={() => handleSelect(result)}
                                                onMouseEnter={() => setSelectedIndex(index)}
                                                className={`px-4 py-3 cursor-pointer flex items-center justify-between transition-colors ${selectedIndex === index ? 'bg-sky-500/10' : 'hover:bg-zinc-800/50'
                                                    }`}
                                            >
                                                <div className="flex items-center space-x-3">
                                                    <div className={`p-2 rounded-lg ${selectedIndex === index ? 'bg-sky-500/20' : 'bg-zinc-800'}`}>
                                                        {getIcon(result.type)}
                                                    </div>
                                                    <div>
                                                        <h4 className="text-sm font-semibold text-white">{result.title}</h4>
                                                        <p className="text-xs text-gray-400 line-clamp-1">{result.description}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <span className="px-2 py-0.5 rounded bg-zinc-800 text-[10px] text-gray-400 font-medium">
                                                        {result.category}
                                                    </span>
                                                    {selectedIndex === index && (
                                                        <FiArrowRight className="text-sky-400 w-4 h-4 animate-in slide-in-from-left-2 duration-300" />
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="p-12 text-center text-gray-500">
                                        <p>No results found for "{query}"</p>
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="px-4 py-2 border-t border-zinc-800 bg-zinc-950/50 flex justify-between items-center">
                                <div className="flex space-x-4 text-[10px] text-gray-500">
                                    <span className="flex items-center"><kbd className="px-1 py-0.5 rounded bg-zinc-900 border border-zinc-700 mr-1">↵</kbd> Select</span>
                                    <span className="flex items-center"><kbd className="px-1 py-0.5 rounded bg-zinc-900 border border-zinc-700 mr-1">↑↓</kbd> Navigate</span>
                                    <span className="flex items-center"><kbd className="px-1 py-0.5 rounded bg-zinc-900 border border-zinc-700 mr-1">esc</kbd> Close</span>
                                </div>
                                <div className="text-[10px] text-sky-400/50 font-medium tracking-wider">
                                    ANTIGRAVITY SEARCH v1.0
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </>
    );
};
